import { useEffect, useState } from 'react'
import { useApp } from '../../context/AppContext'
import {
  TICKET_TYPE_TREE,
  DEFAULT_TICKET_TYPE,
  getTicketType,
} from '../../utils/ticketType'
import {
  IMPACT_LEVELS,
  URGENCY_LEVELS,
  derivePriorityId,
  getPriorityMeta,
} from '../../utils/priorityMatrix'
import ticketHistoryService from '../../services/ticketHistoryService'

const labelOf = (levels, value) =>
  levels.find((l) => l.value === Number(value))?.label || '—'

const EditTicket = ({ ticket, onClosed }) => {
  const { supabase, user } = useApp()
  const [priorities, setPriorities] = useState([])

  const GetInfo = async () => {
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('priority')
        .select('*')

      if (error) throw error

      setPriorities(data)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    GetInfo()
  }, [])

  const [form, setForm] = useState({
    title: '',
    type: DEFAULT_TICKET_TYPE,
    impact: 2,
    urgency: 2,
    priorityId: 2,
    systemOrigin: '',
    description: '',
    archivos: [],
  })

  // 🔄 Cargar datos del ticket
  useEffect(() => {
    if (!ticket) return

    const impact = ticket.impact || 2
    const urgency = ticket.urgency || 2
    setForm({
      title: ticket.title || '',
      type: ticket.type || DEFAULT_TICKET_TYPE,
      impact,
      urgency,
      priorityId: ticket.priorityId || derivePriorityId(impact, urgency),
      systemOrigin: ticket.systemOrigin || '',
      description: ticket.description || '',
      archivos: [],
    })
  }, [ticket])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Impacto/Urgencia recalculan la prioridad derivada.
  const handleMatrixChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => {
      const next = { ...prev, [name]: Number(value) }
      next.priorityId = derivePriorityId(next.impact, next.urgency)
      return next
    })
  }

  const handleFiles = (e) => {
    const files = Array.from(e.target.files)
    setForm((prev) => ({
      ...prev,
      archivos: files,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const payload = {
      id: ticket.id,
      title: form.title,
      description: form.description,
      type: form.type,
      impact: Number(form.impact),
      urgency: Number(form.urgency),
      priorityId: Number(form.priorityId),
      systemOrigin: form.systemOrigin,
    }

    // 👉 Aquí haces tu update con Supabase
    const { error } = await supabase
      .schema('Tickets')
      .from('ticket')
      .update(payload)
      .eq('id', ticket.id)
    if (error) throw error

    // Historial de cambios (audit trail) — solo campos que cambiaron
    const priorityName = (id) =>
      priorities.find((p) => p.id === id)?.name || getPriorityMeta(id).label
    const changedBy = user?.user_metadata?.name || user?.email || 'Usuario'
    await ticketHistoryService.logMany([
      {
        ticketId: ticket.id,
        fieldName: 'Título',
        oldValue: ticket.title,
        newValue: form.title,
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Tipo',
        oldValue: getTicketType(ticket.type).label,
        newValue: getTicketType(form.type).label,
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Impacto',
        oldValue: labelOf(IMPACT_LEVELS, ticket.impact),
        newValue: labelOf(IMPACT_LEVELS, form.impact),
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Urgencia',
        oldValue: labelOf(URGENCY_LEVELS, ticket.urgency),
        newValue: labelOf(URGENCY_LEVELS, form.urgency),
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Prioridad',
        oldValue: priorityName(ticket.priorityId),
        newValue: priorityName(Number(form.priorityId)),
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Origen',
        oldValue: ticket.systemOrigin,
        newValue: form.systemOrigin,
        changedBy,
      },
      {
        ticketId: ticket.id,
        fieldName: 'Descripción',
        oldValue: ticket.description,
        newValue: form.description,
        changedBy,
      },
    ])

    onClosed?.()
  }

  return (
    <form className="container py-1" onSubmit={handleSubmit}>
      {/* Titulo */}
      <div className="mb-2">
        <label className="form-label fw-semibold">Título</label>
        <input
          type="text"
          name="title"
          className="form-control"
          value={form.title}
          onChange={handleChange}
          required
        />
      </div>

      {/* Tipo de registro */}
      <div className="mb-2">
        <label className="form-label fw-semibold">Tipo de Registro</label>
        <select
          name="type"
          className="form-select"
          value={form.type}
          onChange={handleChange}
        >
          {TICKET_TYPE_TREE.map((group) => (
            <optgroup key={group.value} label={group.label}>
              {group.types.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Impacto × Urgencia → Prioridad derivada */}
      <div className="row g-2 mb-2">
        <div className="col-6">
          <label className="form-label fw-semibold">Impacto</label>
          <select
            name="impact"
            className="form-select"
            value={form.impact}
            onChange={handleMatrixChange}
          >
            {IMPACT_LEVELS.map((lvl) => (
              <option key={lvl.value} value={lvl.value}>
                {lvl.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <label className="form-label fw-semibold">Urgencia</label>
          <select
            name="urgency"
            className="form-select"
            value={form.urgency}
            onChange={handleMatrixChange}
          >
            {URGENCY_LEVELS.map((lvl) => (
              <option key={lvl.value} value={lvl.value}>
                {lvl.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 d-flex align-items-center gap-2 mt-1">
          <span className="small text-body-secondary">
            Prioridad calculada:
          </span>
          {(() => {
            const meta = getPriorityMeta(form.priorityId)
            const name =
              priorities.find((p) => p.id === form.priorityId)?.name ||
              meta.label
            return (
              <span
                className="badge"
                style={{ color: meta.color, background: `${meta.color}1a` }}
              >
                <i className="fas fa-circle me-1" style={{ fontSize: '7px' }} />
                {name}
              </span>
            )
          })()}
        </div>
      </div>

      {/* Origen */}
      <div className="mb-2">
        <label className="form-label fw-semibold">Origen</label>
        <input
          type="text"
          name="systemOrigin"
          className="form-control"
          value={form.systemOrigin}
          onChange={handleChange}
        />
      </div>

      {/* Descripcion */}
      <div className="mb-2">
        <label className="form-label fw-semibold">Descripción</label>
        <textarea
          name="description"
          className="form-control"
          rows="4"
          value={form.description}
          onChange={handleChange}
        />
      </div>

      {/* Archivos */}
      <div className="mb-3">
        <label className="form-label fw-semibold">Archivos</label>
        <input
          type="file"
          className="form-control"
          multiple
          onChange={handleFiles}
        />
      </div>

      {/* Botones */}
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => onClosed?.()}
        >
          Cancelar
        </button>

        <button type="submit" className="btn btn-primary">
          Guardar cambios
        </button>
      </div>
    </form>
  )
}

export default EditTicket
